/**
 * v3.15-1039 — Defensive error boundary used to contain render crashes inside
 * a single tab/section. Prevents one bad payroll row, one undefined field, or
 * one missing import from blanking the entire HR page.
 *
 * Usage:
 *   <SectionErrorBoundary label="HR Payroll" testId="payroll-tab">
 *     <PayrollTab />
 *   </SectionErrorBoundary>
 */
import { Component } from 'react';

export class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {

    console.error(`[SectionErrorBoundary:${this.props.label || 'unknown'}]`, error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      return (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-4 text-sm space-y-2"
          data-testid={this.props.testId || 'section-error-boundary'}
        >
          <p className="font-semibold text-amber-800">
            {this.props.label ? `${this.props.label}: ` : ''}Section failed to render.
          </p>
          <p className="text-amber-700 text-[12px] break-all">
            {msg}
          </p>
          <p className="text-[11px] text-amber-600">
            Other tabs and the rest of the page are still usable. Try refreshing,
            or scroll to a different rep / section. If this keeps happening,
            send this message to engineering.
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="text-[11px] underline text-amber-800 hover:text-amber-900"
            data-testid="section-error-retry"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default SectionErrorBoundary;
